/**
 * Pending-invite detail — the detail panel's "invite-detail" mode, opened
 * from a pending row in the member list (grouped inline under its role,
 * beside active members — see members.view.ts). DUMB atomic
 * SchmancyElement: reads `inviteDetailView` computed (the live pending
 * Member row addressed by token) and writes intents through members.state's
 * membersAction$ channel (members.sync.ts, web-side, performs the write) +
 * detailState. Derives nothing in render().
 *
 * Actions: resend (revoke + re-create via createInvite, fresh token; 60s
 * cooldown after firing) and revoke (flips status to revoked). Both close
 * the panel on success rather than address the replacement/deleted row
 * ahead of the live members listener catching up — the member list's own
 * pending row is where an admin returns to look at the result.
 *
 * Edit (name + role, via editInvite): the name field and <select-role> below
 * commit directly on change — no separate draft, no confirm dialog. Unlike
 * an active member's role change (member-detail.view.ts), a pending
 * invitee holds no power yet, so correcting either field carries no
 * immediate access consequence; the panel stays open after a successful
 * edit so an admin can keep correcting. Both controls read `invite`
 * straight off the live `inviteDetailView` computed, so they always show
 * the current server-confirmed value, never a stale local draft.
 */

import { awaitByRequestId$ } from "@mhmo91/rx";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { firstValueFrom, interval, take, takeUntil, tap } from "rxjs";
import { activeActor } from "../../auth/actor.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	detailState,
	inviteDetailView,
	membersAction$,
	membersResult$,
} from "../members.state.ts";
import "../roles/permission-picker.view.ts";
import "../roles/select-role.view.ts";

@schmancyElement("invite-detail")
export class InviteDetail extends LocalizeMixin(SchmancyElement) {
	// Local UI-only cooldown after firing resend — not derived from any state singleton.
	@state() private resendCooldown = 0;

	// Round-trip feedback for the callables this panel fires — blocks a
	// second click/change firing a duplicate resend/revoke/edit and drives
	// each control's spinner/disabled state.
	@state() private resendInFlight = false;
	@state() private revokeInFlight = false;
	@state() private editInFlight = false;

	@state() private nameDraft: string | null = null;
	@state() private failedName = false;
	@state() private failedRoleId = "";
	@state() private pendingRoleId = "";
	@state() private resendFailed = false;
	@state() private revokeFailed = false;

	private async commitEdit(patch: {
		roleId?: string;
		displayName?: string;
	}): Promise<void> {
		if (this.editInFlight || this.resendInFlight || this.revokeInFlight) return;
		const current = inviteDetailView.get();
		if (!current) return;
		this.editInFlight = true;
		this.pendingRoleId = patch.roleId ?? "";
		try {
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "editInvite",
				requestId,
				token: current.invite.token,
				patch,
			});
			if (!(await answer).ok) {
				this.failedName = !!patch.displayName;
				this.failedRoleId = patch.roleId ?? "";
			} else {
				this.failedName = false;
				this.failedRoleId = "";
			}
		} catch {
			this.failedName = !!patch.displayName;
			this.failedRoleId = patch.roleId ?? "";
		} finally {
			this.editInFlight = false;
			this.pendingRoleId = "";
		}
	}

	private async resend(): Promise<void> {
		if (
			this.resendCooldown > 0 ||
			this.resendInFlight ||
			this.revokeInFlight ||
			this.editInFlight
		)
			return;
		const current = inviteDetailView.get();
		if (!current) return;
		const invite = current.invite;
		this.resendInFlight = true;
		this.resendCooldown = 60;
		interval(1000)
			.pipe(
				take(60),
				tap(() => this.resendCooldown--),
				takeUntil(this.disconnecting),
			)
			.subscribe();
		try {
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "resendInvite",
				requestId,
				token: invite.token,
				draft: {
					email: invite.email,
					displayName: invite.displayName,
					roleId: invite.roleId,
					scope:
						invite.scope.kind === "all-events"
							? { kind: "all-events", orgId: activeActor.get()?.orgId ?? "" }
							: { kind: "events", eventIds: invite.scope.eventIds },
				},
			});
			if (!(await answer).ok) this.resendFailed = true;
			else detailState.replace({ kind: "none" });
		} catch {
			this.resendFailed = true;
		} finally {
			this.resendInFlight = false;
		}
	}

	private async revoke(): Promise<void> {
		if (this.resendInFlight || this.revokeInFlight || this.editInFlight) return;
		const current = inviteDetailView.get();
		if (!current) return;
		this.revokeInFlight = true;
		try {
			if (
				!(await confirm({
					title: this.t("admin.members.inviteDetail.revokeConfirmTitle"),
					message: this.t("admin.members.inviteDetail.revokeConfirmMessage"),
					confirmText: this.t("admin.members.inviteDetail.revokeConfirmText"),
					cancelText: this.t(
						"admin.members.inviteDetail.revokeConfirmCancelText",
					),
					variant: "danger",
				}))
			)
				return;
			const requestId = crypto.randomUUID();
			const answer = firstValueFrom(
				awaitByRequestId$(membersResult$, requestId).pipe(
					takeUntil(this.disconnecting),
				),
			);
			membersAction$.next({
				kind: "revokeInvite",
				requestId,
				token: current.invite.token,
			});
			if (!(await answer).ok) this.revokeFailed = true;
			else detailState.replace({ kind: "none" });
		} catch {
			this.revokeFailed = true;
		} finally {
			this.revokeInFlight = false;
		}
	}

	override render(): TemplateResult {
		const v = inviteDetailView.get();
		if (!v) return html``;
		const {
			invite,
			grants,
			invitedLabel,
			expiresLabel,
			expiryUrgencyLabel,
			expiryTone,
			isAllEvents,
			scopeEventNames,
		} = v;

		return html`
			<div class="flex flex-col h-full max-h-[85dvh] lg:max-h-none">
				<div class="flex items-center gap-3 px-5 pt-5 pb-3">
					<schmancy-avatar class="w-11 h-11 shrink-0" aria-hidden="true" color="neutral" icon="schedule"></schmancy-avatar>
					<div class="min-w-0 flex-1">
						<schmancy-typography type="title" token="md" class="truncate">${invite.displayName}</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant block truncate">${invite.email}</schmancy-typography>
					</div>
					<schmancy-icon-button
						icon="close"
						?disabled=${this.editInFlight || this.resendInFlight || this.revokeInFlight}
						variant="text"
						aria-label=${this.t("admin.members.inviteDetail.closeAriaLabel")}
						@click=${() => detailState.replace({ kind: "none" })}
					></schmancy-icon-button>
				</div>

				<div ${overflowWithin()} class="px-5 py-2 flex-1 min-h-0 flex flex-col gap-4">
					<!-- Status -->
					<schmancy-surface type="warning" class="flex flex-col gap-2 rounded-xl px-4 py-3">
						<div class="flex items-center gap-2">
							<schmancy-icon class="text-base" aria-hidden="true">schedule</schmancy-icon>
							<schmancy-typography type="label" token="md">${this.t("admin.members.inviteDetail.pendingLabel")}</schmancy-typography>
						</div>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.members.inviteDetail.sentOn", { date: invitedLabel })}</schmancy-typography>
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"text-error-default": expiryTone === "error",
								"text-warning-default": expiryTone === "warning",
								"text-surface-onVariant": expiryTone === "default",
							})}
						>
							${expiryUrgencyLabel} · ${expiresLabel}
						</schmancy-typography>
					</schmancy-surface>

					<schmancy-input
						label=${this.t("admin.members.common.nameLabel")}
						.value=${this.nameDraft ?? invite.displayName}
						@input=${(event: CustomEvent<{ value: string }>) => {
							if (!this.editInFlight) this.nameDraft = event.detail.value;
						}}
						?disabled=${this.editInFlight || this.resendInFlight || this.revokeInFlight}
						@change=${(e: CustomEvent<{ value: string }>) => {
							const next = e.detail.value.trim();
							if (!next || next === invite.displayName) return;
							this.commitEdit({ displayName: next });
						}}
					><schmancy-icon-button slot="actions" icon=${this.failedName ? "error" : "check"} .busy=${this.editInFlight && !this.pendingRoleId} ?readonly=${!this.failedName} aria-label=${this.t(this.editInFlight && !this.pendingRoleId ? "admin.common.save" : this.failedName ? "admin.common.retrySave" : "admin.members.inviteDetail.editedToast")} @click=${() => this.nameDraft?.trim() && this.commitEdit({ displayName: this.nameDraft.trim() })}></schmancy-icon-button></schmancy-input>

					<div class="flex flex-col gap-2">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.roleLabel")}</schmancy-typography>
						<select-role
							.value=${invite.roleId}
							?disabled=${this.resendInFlight || this.revokeInFlight}
							.pendingRoleId=${this.pendingRoleId}
							.failedRoleId=${this.failedRoleId}
							@change=${(e: CustomEvent<{ value: string }>) => {
								if (e.detail.value === invite.roleId) return;
								this.commitEdit({ roleId: e.detail.value });
							}}
						></select-role>
						<permission-picker readonly .selected=${new Set(grants)}></permission-picker>
					</div>

					<div class="flex flex-col gap-2">
						<div class="flex items-center gap-1.5">
							<schmancy-icon class="text-surface-onVariant text-base" aria-hidden="true">event</schmancy-icon>
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.members.common.eventAccessLabel")}</schmancy-typography>
						</div>
						${when(
							isAllEvents,
							() => html`
								<schmancy-typography type="body" token="sm">
									${this.t("admin.members.inviteDetail.allEventsCurrentFuture")}
								</schmancy-typography>
							`,
							() => html`
								<div class="flex flex-wrap gap-1.5">
									${repeat(
										scopeEventNames,
										(name) => name,
										(name) => html`
											<schmancy-surface type="container" class="rounded-full px-3 py-1">
												<schmancy-typography type="label" token="sm">${name}</schmancy-typography>
											</schmancy-surface>
										`,
									)}
								</div>
							`,
						)}
					</div>
				</div>

				<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 px-5 py-4 pt-2 flex flex-col gap-2">
					<schmancy-button variant="filled" width="full" .busy=${this.resendInFlight} color=${this.resendFailed ? "error" : "primary"} ?disabled=${(!this.resendInFlight && this.resendCooldown > 0) || this.editInFlight || this.revokeInFlight} @click=${() => this.resend()}>
						<schmancy-icon slot="prefix">send</schmancy-icon>
						${this.resendInFlight ? this.t("admin.members.inviteDetail.resendButton") : this.resendCooldown > 0 ? `${this.resendFailed ? `${this.t("admin.common.retry")} · ` : ""}${this.t("admin.members.inviteDetail.resendButtonCooldown", { seconds: this.resendCooldown })}` : this.t(this.resendFailed ? "admin.common.retrySend" : "admin.members.inviteDetail.resendButton")}
					</schmancy-button>
					<div class="flex flex-col gap-1">
						<schmancy-button variant="outlined" color="error" width="full" .busy=${this.revokeInFlight} ?disabled=${this.editInFlight || this.resendInFlight} @click=${() => this.revoke()}>
							<schmancy-icon slot="prefix">cancel</schmancy-icon>
							${this.t(this.revokeFailed ? "admin.common.retry" : "admin.members.inviteDetail.revokeButton")}
						</schmancy-button>
						<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${this.t("admin.members.inviteDetail.revokeHint")}</schmancy-typography>
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"invite-detail": InviteDetail;
	}
}
